"use client";

import {AiFillCloseCircle, AiOutlinePaperClip} from "react-icons/ai";

interface FileData {
  id: string;
  name: string;
  file: string;
  fileSize: string;
}

interface FileChipProps {
  file: FileData;
  onRemove?: () => void;
  classNames?: string;
}

const MIME_TYPES_BY_EXTENSION: Record<string, string> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  doc: "application/msword",
  docx:
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  txt: "text/plain"
};

function getMimeType(fileName: string): string {
  const extension = fileName.split(".").pop()?.toLowerCase() ?? "";
  return MIME_TYPES_BY_EXTENSION[extension] ?? "application/octet-stream";
}

export function FileChip({file, onRemove, classNames = ""}: FileChipProps) {
  // Firebase Storage serves these files with a generic Content-Type, which
  // makes Chrome's built-in PDF viewer refuse to render them (Firefox still
  // sniffs and renders fine). Re-fetch and reopen as a correctly-typed blob
  // so Chrome opens it in its viewer instead of showing a download/error.
  const handleOpen = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    try {
      const response = await fetch(file.file);
      const blob = await response.blob();
      const typedBlob = new Blob([blob], {type: getMimeType(file.name)});
      const objectUrl = URL.createObjectURL(typedBlob);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch {
      window.open(file.file, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      className={`mr-2 flex h-[60px] items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm ${
        classNames ? classNames : "w-[155px]"
      }`}
    >
      <a
        href={file.file}
        onClick={handleOpen}
        target="_blank"
        rel="noopener noreferrer"
        className="flex flex-grow items-center space-x-2 overflow-hidden"
        title={file.name}
      >
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center">
          <AiOutlinePaperClip className="h-7 w-7 text-yellow-500" />
        </div>
        <div className="flex max-w-[90px] flex-col overflow-hidden">
          <p className="truncate text-sm font-semibold text-black">
            {file.name}
          </p>
          <p className="text-xs text-gray-500">{file.fileSize}</p>
        </div>
      </a>
      {/* Only show remove icon if onRemove is provided */}
      {onRemove && (
        <button
          onClick={onRemove}
          className="ml-2 flex-shrink-0 text-red-500 hover:text-red-600"
          title="Remove"
        >
          <AiFillCloseCircle className="h-6 w-6" />
        </button>
      )}
    </div>
  );
}
